import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AudioLines, BookOpen, FileText, Image, LayoutGrid, Loader2, Video } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { chapterApi, type ChapterLibraryStatus } from '@/api/chapterApi'
import { friendlyMessage } from '@/api/http'
import { ALL_PROJECTS_VALUE, ProjectCombobox } from '@/components/project/ProjectCombobox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Pagination } from '@/components/ui/pagination'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { statusLabel, type StatusTone } from '@/lib/statusLabel'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import { useUiStore } from '@/stores/uiStore'
import type { Chapter } from '@/types/nf'
import { formatCount, formatDateTime } from '@/utils/format'

const PAGE_SIZE = 20
const statusOptions = ['all', 'done', 'stopped', 'awaiting_confirm'] as const satisfies readonly ChapterLibraryStatus[]
const libraryModalityKeys = ['screenplay', 'storyboard', 'panel_image', 'audio', 'video'] as const
type LibraryModalityKey = (typeof libraryModalityKeys)[number]

const libraryModalityIcons = {
  screenplay: FileText,
  storyboard: LayoutGrid,
  panel_image: Image,
  audio: AudioLines,
  video: Video,
} satisfies Record<LibraryModalityKey, typeof FileText>

export function libraryModalityStates(chapter: Chapter) {
  const ready = new Set(Array.isArray(chapter.available_modalities) ? chapter.available_modalities : [])
  return libraryModalityKeys.map((key) => ({
    key,
    ready: ready.has(key),
    labelKey: `library.modalities.${key}`,
  }))
}

function chapterStatus(chapter: Chapter) {
  const cascadeState = typeof chapter.cascade_state === 'string' ? chapter.cascade_state : null
  return cascadeState ?? chapter.status ?? 'unknown'
}

const libraryToneClass: Record<StatusTone, string> = {
  success: 'border-[var(--nf-st-done)] text-[var(--nf-st-done)]',
  running: 'border-[var(--nf-st-running)] text-[var(--nf-st-running)]',
  warning: 'border-[var(--nf-st-review)] text-[var(--nf-st-review)]',
  danger: 'border-[var(--nf-st-failed)] text-[var(--nf-st-failed)]',
  partial: 'border-[var(--nf-st-partial)] text-[var(--nf-st-partial)]',
  muted: 'border-muted-foreground text-muted-foreground',
  unknown: 'border-muted-foreground text-muted-foreground',
}

function ModalityIcons({ chapter }: { chapter: Chapter }) {
  const { t } = useTranslation()
  const states = libraryModalityStates(chapter)

  return (
    <div className="flex shrink-0 items-center gap-1" aria-label={t('library.modalitiesLabel')}>
      {states.map((item) => {
        const Icon = libraryModalityIcons[item.key]
        const label = t(item.labelKey)
        const stateLabel = t(item.ready ? 'library.modalityReady' : 'library.modalityMissing', { modality: label })
        return (
          <span
            key={item.key}
            aria-label={stateLabel}
            title={stateLabel}
            role="img"
          >
            <Icon
              aria-hidden="true"
              className={cn(
                'size-4 transition-colors',
                item.ready ? 'text-primary' : 'text-muted-foreground/35',
              )}
            />
          </span>
        )
      })}
    </div>
  )
}

export default function Library() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const { projects, current, loading: projectLoading, error: projectError, load, select } = useProjectStore()
  const currentBranchId = useUiStore((s) => s.currentBranchId)
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [status, setStatus] = useState<ChapterLibraryStatus>('all')
  const [page, setPage] = useState(1)

  useEffect(() => {
    if (user?.user_id) void load(user.user_id)
  }, [load, user?.user_id])

  useEffect(() => {
    if (!projects.length) return
    if (selectedProjectId === ALL_PROJECTS_VALUE) return
    if (selectedProjectId && projects.some((project) => project.id === selectedProjectId)) return
    const nextProjectId = current && projects.some((project) => project.id === current.id) ? current.id : projects[0].id
    setSelectedProjectId(nextProjectId)
    if (current?.id !== nextProjectId) void select(nextProjectId)
  }, [current, projects, select, selectedProjectId])

  const chaptersQuery = useQuery({
    queryKey: ['chapters', 'library', selectedProjectId, currentBranchId, status, page, PAGE_SIZE],
    queryFn: () =>
      chapterApi.listPaged(selectedProjectId ?? '', {
        branchId: currentBranchId,
        status,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: Boolean(selectedProjectId),
  })

  const pageCount = useMemo(() => {
    const total = chaptersQuery.data?.total ?? 0
    return Math.max(1, Math.ceil(total / PAGE_SIZE))
  }, [chaptersQuery.data?.total])
  const isAllProjects = selectedProjectId === ALL_PROJECTS_VALUE

  const onProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId)
    setPage(1)
    if (projectId !== ALL_PROJECTS_VALUE) void select(projectId)
  }

  const onStatusChange = (value: ChapterLibraryStatus) => {
    setStatus(value)
    setPage(1)
  }

  const openChapter = (chapter: Chapter) => {
    const projectId = isAllProjects ? chapter.project_id : selectedProjectId
    if (!projectId) return
    navigate(`/projects/${encodeURIComponent(projectId)}/reader/ch/${encodeURIComponent(String(chapter.chapter_number))}`)
  }

  const loading = projectLoading || chaptersQuery.isLoading || chaptersQuery.isFetching
  const error = projectError || (chaptersQuery.error ? friendlyMessage(chaptersQuery.error, t('common.serviceError')) : null)
  const chapters = chaptersQuery.data?.items ?? []
  const total = chaptersQuery.data?.total ?? 0

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="m-0 text-3xl font-semibold tracking-normal">{t('library.title')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('library.subtitle')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <ProjectCombobox
              projects={projects}
              value={selectedProjectId}
              onChange={onProjectChange}
              isLoading={projectLoading}
              ariaLabel={t('library.selectProject')}
              includeAllProjects
            />
            <Select value={status} onValueChange={(value) => onStatusChange(value as ChapterLibraryStatus)}>
              <SelectTrigger aria-label={t('library.statusFilter')} className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {t(`library.status.${option}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <div className="rounded-lg border border-[var(--nf-hairline)] bg-[var(--nf-surface-1)]">
          <div className="flex items-center justify-between gap-3 border-b border-[var(--nf-hairline)] px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <BookOpen className="size-4" />
              <span>{t('library.total', { total: formatCount(total) })}</span>
            </div>
            {loading ? <Loader2 className="size-4 animate-spin text-primary" /> : null}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('library.columns.chapter')}</TableHead>
                {isAllProjects ? <TableHead>{t('library.columns.project')}</TableHead> : null}
                <TableHead>{t('library.columns.status')}</TableHead>
                <TableHead className="text-right">{t('library.columns.words')}</TableHead>
                <TableHead className="text-right">{t('library.columns.updated')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chapters.length ? (
                chapters.map((chapter) => {
                  const displayStatus = chapterStatus(chapter)
                  const visual = statusLabel(displayStatus, 'chapter')
                  return (
                    <TableRow
                      key={chapter.id}
                      className="cursor-pointer"
                      onClick={() => openChapter(chapter)}
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          openChapter(chapter)
                        }
                      }}
                    >
                      <TableCell>
                        <div className="flex min-w-0 flex-col gap-1">
                          <div className="flex min-w-0 items-center gap-3">
                            <span className="truncate font-medium">
                              {t('library.chapterTitle', {
                                number: formatCount(chapter.chapter_number),
                                title: chapter.title || t('library.untitled'),
                              })}
                            </span>
                            <ModalityIcons chapter={chapter} />
                          </div>
                          {chapter.summary?.trim() ? (
                            <span className="truncate text-xs text-muted-foreground">{chapter.summary}</span>
                          ) : null}
                        </div>
                      </TableCell>
                      {isAllProjects ? (
                        <TableCell>
                          <span className="truncate text-sm text-muted-foreground">{chapter.project_title || chapter.project_id}</span>
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <Badge variant="outline" className={cn('border', libraryToneClass[visual.tone])}>
                          {t(visual.textKey, visual.textOptions)}
                        </Badge>
                      </TableCell>
                      <TableCell className="nf-mono text-right">
                        {typeof chapter.word_count === 'number' ? formatCount(chapter.word_count) : t('library.noWords')}
                      </TableCell>
                      <TableCell className="nf-mono text-right">{formatDateTime(chapter.updated_at)}</TableCell>
                    </TableRow>
                  )
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={isAllProjects ? 5 : 4} className="h-32 text-center text-muted-foreground">
                    {loading ? t('common.loading') : t('library.empty')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="px-4 pb-4">
            <Pagination
              page={page - 1}
              pageCount={pageCount}
              pageSize={PAGE_SIZE}
              total={total}
              onPage={(nextPage) => setPage(nextPage + 1)}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
